import React, { useMemo, useState } from "react";
import { Autocomplete, Box, Chip, TextField } from "@mui/material";
import { Column, getColumnTypeForPermission } from "../../types/table";
import { useSelector } from "react-redux";
import { RootState } from "../../support/store";
import { selectSalesforcePermissionById } from "../../support/slices/salesforcePermissionsSlice";
import StbModal from "../security-toolbox-ui/StbModal";


interface TableColumnManagerProps {
  columns?: Column[];
  modalOpen: boolean;
  cancelSetColumns: () => Promise<void>;
  setColumns: (columns: {id: string, name: string}[]) => void;
}

const OppTableColumnManagerModal: React.FC<TableColumnManagerProps> = ({
  modalOpen,
  columns,
  cancelSetColumns,
  setColumns
}: TableColumnManagerProps) => {
  const permissions = useSelector((state: RootState) => selectSalesforcePermissionById(state, 'Opportunity'));

  // Chips
  const [selectedColumns, setSelectedColumns] = useState<{id: string, name: string}[]>(columns);

  // Autocomplete
  const [selectedValue, setSelectedValue] = useState<{id: string, name: string} | null>(null);
  const [inputFieldValue, setInputFieldValue] = useState("");
  const availableColumns = useMemo(() => {
    return permissions.map(permission => ({
      id: permission.value,
      name: permission.name,
      columnType: getColumnTypeForPermission(permission),
      sortable: !!(permission?.source === 'salesforce')
    }) as Column)
    .filter(col => !selectedColumns.map((col) => col.id).includes(col.id));
  }, [permissions, selectedColumns]);

  const handleInputFieldChange = (name: string) => {
    setInputFieldValue(name)
  }

  const handleSelectionChange = (selected: {id: string, name: string} | null) => {
    if (!selected || selectedColumns.find(col => col.id === selected.id)) return;

    setSelectedColumns(prev => [...prev, selected]);
    setSelectedValue(null);
    setInputFieldValue("");
  }

  const handleChipOnDelete = (column: {id: string, name: string}) => {
    setSelectedColumns(prev => prev.filter(col => col.id !== column.id));
  };

  const handleUpdateButtonOnClick = async () => {
    setColumns(selectedColumns);
  };

  return (
    <StbModal
      title="Manage Columns"
      submitLabel="Update Columns"
      submitButtonRole="save"
      open={modalOpen}
      onClose={cancelSetColumns}
      onSubmit={handleUpdateButtonOnClick}
    >
      <div className="flex flex-col">
        <Autocomplete
          options={availableColumns}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedValue}
          onChange={(event, newValue) => {
            handleSelectionChange(newValue);
          }}
          inputValue={inputFieldValue}
          onInputChange={(event, newInputValue) => {
            handleInputFieldChange(newInputValue);
          }}

          renderInput={(params) => <TextField {...params} name="Select Table Column" />}
          fullWidth
          sx={{ pb: 2 }}
        />
        <Box 
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            position: 'relative',
            pb: 2
          }}
        >
          {selectedColumns.filter(c => c.id !== 'selected').map((column) => (
            <Chip
              label={column.name}
              key={column.id}
              onDelete={!['SE Status', 'Opportunity'].includes(column.name) ? () => handleChipOnDelete(column) : undefined}
              color={['SE Status', 'Opportunity'].includes(column.name) ? 'primary' : 'default'}
              sx={{
                m: 0.5,
                cursor: 'move'
              }}
            />
          ))}
        </Box>
      </div>
    </StbModal>
  );
};

export default OppTableColumnManagerModal;